"use client";

import { AlertTriangle, ChevronsUpDown, ExternalLink, Filter, Info, Sparkles } from "lucide-react";
import * as React from "react";
import { List } from "react-window";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandInput } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useIsMobile } from "@/hooks/use-mobile";
import { useFavoriteModels } from "@/hooks/useFavoriteModels";
import { useApi } from "@/lib/fetch-client";
import { formatPrice, formatContextSize, getProviderForModel } from "@/lib/model-utils";
import { cn } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
import { getProviderIcon, providerLogoUrls } from "@llmgateway/shared/components";
import { ImageEstimateCard, ModelEntryRowComponent, ROW_HEIGHT, createModelEntries, estimateImageCost, filterModelEntries, formatImageCost, formatPerSecondPrice, getAvailableCapabilities, getAvailableProviders, getCapabilityIconConfig, getCapabilityLabel, getMappingCapabilities, getMappingPriceInfo, getMaxPerSecondPrice, getMinImageCostEstimate, getMinPerSecondPrice, getRootAggregateInfo, isModelUnstable, type FilterState } from "./model-selector-helpers";
import type { ApiModel, ApiModelProviderMapping, ApiProvider } from "@/lib/fetch-models";
import type { ProviderId } from "@llmgateway/models";
interface ModelSelectorProps {
  models: ApiModel[];
  providers: ApiProvider[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  mode?: "chat" | "video" | "image" | "audio";
  isOptionDisabled?: (value: string) => boolean;
  getOptionDisabledReason?: (value: string) => string | undefined;
}
export function ModelSelector({
  models,
  providers,
  value,
  onValueChange,
  placeholder,
  mode = "chat",
  isOptionDisabled,
  getOptionDisabledReason
}: ModelSelectorProps) {
  const {
    t
  } = useI18n();
  const {
    isFavorite,
    toggleFavorite
  } = useFavoriteModels();
  const isMobile = useIsMobile();
  const api = useApi();
  const {
    data: providerKeysData
  } = api.useQuery("get", "/keys/provider/active");
  const providersWithKeys = React.useMemo(() => {
    const keys = providerKeysData?.providerKeys ?? [];
    return new Set(keys.filter(k => k.status === "active").map(k_0 => k_0.provider as string));
  }, [providerKeysData]);
  const [open, setOpen] = React.useState(false);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [focusedIndex, setFocusedIndex] = React.useState(-1);
  const listContainerRef = React.useRef<HTMLDivElement>(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [selectedDetails, setSelectedDetails] = React.useState<{
    model: ApiModel;
    mapping?: ApiModelProviderMapping;
    provider?: ApiProvider;
  } | null>(null);
  const [previewEntry, setPreviewEntry] = React.useState<{
    model: ApiModel;
    mapping?: ApiModelProviderMapping;
    provider?: ApiProvider;
    isRoot?: boolean;
  } | null>(null);
  const [filters, setFilters] = React.useState<FilterState>({
    providers: [],
    capabilities: [],
    priceRange: "all",
    hideUnstable: true,
    showOnlyRoot: false,
    showFavoritesOnly: false,
    showOnlyWithKeys: false
  });
  const [previewExpandTokens, setPreviewExpandTokens] = React.useState(false);
  const [selectedExpandTokens, setSelectedExpandTokens] = React.useState(false);
  const isImageGenModel = (model?: ApiModel | null) => !!model?.output && model.output.includes("image");
  const previewIsImageGen = isImageGenModel(previewEntry?.model);
  const selectedIsImageGen = isImageGenModel(selectedDetails?.model);
  React.useEffect(() => {
    setPreviewExpandTokens(false);
  }, [previewEntry?.model]);
  React.useEffect(() => {
    setSelectedExpandTokens(false);
  }, [selectedDetails?.model]);

  // Parse value as provider/model-id (preferred). Fallback to model id only.
  // Supports region suffix: "alibaba/deepseek-v3.2:cn-beijing" or
  // "aws-bedrock/claude-haiku-4-5:global". The region is the substring after
  // the last ":" (model.id never contains ":"; upstream modelNames may).
  const raw = value ?? "";
  const [selectedProviderId, selectedModelIdRaw] = raw.includes("/") ? raw.split("/") as [string, string] : ["", raw];
  // model.id never contains ":", so split on ":" is safe for the URL form
  // (provider/model.id[:region]). Look up by canonical (root) id only.
  const lastColonIdx = selectedModelIdRaw.lastIndexOf(":");
  const selectedRegion = lastColonIdx > -1 ? selectedModelIdRaw.slice(lastColonIdx + 1) : undefined;
  const selectedModelId = lastColonIdx > -1 ? selectedModelIdRaw.slice(0, lastColonIdx) : selectedModelIdRaw;
  const selectedModel = models.find(m => m.id === selectedModelId);
  const selectedProviderDef = providers.find(p => p.id === selectedProviderId);
  // Strict (providerId, region) match — no loose fallbacks that would pick
  // the first regional variant when no region was requested.
  const selectedMapping = selectedRegion ? selectedModel?.mappings.find(p_0 => p_0.providerId === selectedProviderId && p_0.region === selectedRegion) : selectedModel?.mappings.find(p_1 => p_1.providerId === selectedProviderId && !p_1.region);
  const selectedEntryKey = selectedModel && selectedProviderId && selectedMapping ? `${selectedProviderId}-${selectedModel.id}-${selectedMapping.region ?? ""}` : selectedModel ? selectedModel.id : "";
  const allEntries = React.useMemo(() => createModelEntries(models, providers), [models, providers]);

  // Defer search input value to keep typing responsive with large lists
  const deferredSearch = React.useDeferredValue(searchQuery);
  const availableProviders = React.useMemo(() => getAvailableProviders(allEntries, providers), [allEntries, providers]);
  const availableCapabilities = React.useMemo(() => getAvailableCapabilities(allEntries), [allEntries]);
  const filteredEntries = React.useMemo(() => filterModelEntries({
    entries: allEntries,
    deferredSearch,
    filters,
    isFavorite,
    providersWithKeys
  }), [allEntries, deferredSearch, filters, isFavorite, providersWithKeys]);
  const providerFacetEntries = React.useMemo(() => filterModelEntries({
    entries: allEntries,
    deferredSearch,
    filters: {
      ...filters,
      providers: []
    },
    isFavorite,
    providersWithKeys
  }), [allEntries, deferredSearch, filters, isFavorite, providersWithKeys]);
  const providerSidebarOptions = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const entry of providerFacetEntries) {
      if (!entry.mapping) {
        continue;
      }
      counts.set(entry.mapping.providerId, (counts.get(entry.mapping.providerId) ?? 0) + 1);
    }
    return providers.filter(provider => counts.has(provider.id)).map(provider_0 => ({
      ...provider_0,
      count: counts.get(provider_0.id) ?? 0
    })).sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count;
      }
      return (a.name ?? a.id).localeCompare(b.name ?? b.id);
    });
  }, [providerFacetEntries, providers]);
  const updateFilter = (key: keyof FilterState, value_0: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value_0
    }));
  };
  const toggleProviderFilter = (providerId: string) => {
    setFilters(prev_0 => ({
      ...prev_0,
      providers: prev_0.providers.includes(providerId) ? prev_0.providers.filter(id => id !== providerId) : [...prev_0.providers, providerId],
      // If selecting providers, we probably don't want to show root models only
      showOnlyRoot: false
    }));
  };
  const toggleCapabilityFilter = (capability: string) => {
    setFilters(prev_1 => ({
      ...prev_1,
      capabilities: prev_1.capabilities.includes(capability) ? prev_1.capabilities.filter(cap => cap !== capability) : [...prev_1.capabilities, capability]
    }));
  };
  const clearFilters = () => {
    setFilters({
      providers: [],
      capabilities: [],
      priceRange: "all",
      hideUnstable: true,
      showOnlyRoot: false,
      showFavoritesOnly: false,
      showOnlyWithKeys: false
    });
  };
  const toggleSidebarProviderFilter = (providerId_0: string) => {
    setFilters(prev_2 => {
      const isOnlyActiveProvider = prev_2.providers.length === 1 && prev_2.providers[0] === providerId_0;
      return {
        ...prev_2,
        providers: isOnlyActiveProvider ? [] : [providerId_0],
        showOnlyRoot: false
      };
    });
  };
  const hasActiveFilters = filters.providers.length > 0 || filters.capabilities.length > 0 || filters.priceRange !== "all" || !filters.hideUnstable || filters.showOnlyRoot || filters.showFavoritesOnly || filters.showOnlyWithKeys;
  const getProviderLogo = (providerId_1: ProviderId) => {
    const LogoComponent = providerLogoUrls[providerId_1];
    if (LogoComponent) {
      return <LogoComponent className="h-10 w-10 object-contain" />;
    }
    const IconComponent = getProviderIcon(providerId_1);
    return IconComponent ? <IconComponent className="h-10 w-10" /> : <div className="h-10 w-10 bg-gray-200 rounded" />;
  };
  const getCompactProviderLogo = (provider_1: ApiProvider) => {
    const LogoComponent_0 = providerLogoUrls[provider_1.id as ProviderId];
    if (LogoComponent_0) {
      return <LogoComponent_0 className="h-4 w-4 object-contain" />;
    }
    const IconComponent_0 = getProviderIcon(provider_1.id);
    return IconComponent_0 ? <IconComponent_0 className="h-4 w-4" style={{
      color: provider_1.color ?? undefined
    }} /> : <span className="text-[10px] font-semibold uppercase">
				{(provider_1.name ?? provider_1.id).slice(0, 1)}
			</span>;
  };

  // Keep desktop preview in sync with the currently selected model when opening
  React.useEffect(() => {
    if (!open) {
      return;
    }
    if (!selectedModel) {
      setPreviewEntry(null);
      return;
    }

    // Prefer provider-specific entry when a provider is selected.
    // Match on region to distinguish regional variants.
    let entry_0 = selectedProviderId && allEntries.find(e => !e.isRoot && e.model.id === selectedModel.id && e.mapping?.providerId === selectedProviderId && (!selectedMapping || e.mapping?.region === selectedMapping.region));

    // Fallback to root entry for the selected model
    entry_0 ??= allEntries.find(e => e.isRoot && e.model.id === selectedModel.id);

    // Fallback to first filtered entry
    if (!entry_0 && filteredEntries.length > 0) {
      entry_0 = filteredEntries[0];
    }
    setPreviewEntry(entry_0 ? {
      model: entry_0.model,
      mapping: entry_0.mapping,
      provider: entry_0.provider,
      isRoot: entry_0.isRoot
    } : null);
  }, [open, selectedModel, selectedProviderId, selectedMapping, allEntries, filteredEntries]);
  React.useEffect(() => {
    setFocusedIndex(-1);
  }, [open, searchQuery]);
  const handleInputKeyDown = React.useCallback((e_0: React.KeyboardEvent<HTMLInputElement>) => {
    if (e_0.key === "ArrowDown") {
      e_0.preventDefault();
      e_0.stopPropagation();
      const next = Math.min(focusedIndex + 1, filteredEntries.length - 1);
      setFocusedIndex(next);
      const scrollEl = listContainerRef.current?.firstElementChild as HTMLElement | null;
      scrollEl?.scrollTo({
        top: next * ROW_HEIGHT
      });
    } else if (e_0.key === "ArrowUp") {
      e_0.preventDefault();
      e_0.stopPropagation();
      const prev_3 = Math.max(focusedIndex - 1, 0);
      setFocusedIndex(prev_3);
      const scrollEl_0 = listContainerRef.current?.firstElementChild as HTMLElement | null;
      scrollEl_0?.scrollTo({
        top: prev_3 * ROW_HEIGHT
      });
    } else if (e_0.key === "Enter") {
      e_0.preventDefault();
      e_0.stopPropagation();
      const entry_1 = filteredEntries[focusedIndex];
      if (!entry_1) {
        return;
      }
      const {
        model: model_0,
        mapping,
        isRoot
      } = entry_1;
      const value_1 = isRoot ? model_0.id : `${mapping!.providerId}/${model_0.id}${mapping!.region ? `:${mapping!.region}` : ""}`;
      const disabled = isRoot ? isOptionDisabled?.(model_0.id) ?? false : isOptionDisabled?.(value_1) ?? false;
      if (disabled) {
        return;
      }
      onValueChange?.(value_1);
      setOpen(false);
    }
  }, [focusedIndex, filteredEntries, isOptionDisabled, onValueChange]);
  const rowProps = React.useMemo(() => ({
    entries: filteredEntries,
    focusedIndex,
    setFocusedIndex,
    selectedEntryKey,
    isFavorite,
    toggleFavorite,
    onSelect: (value_2: string) => {
      onValueChange?.(value_2);
      setOpen(false);
    },
    setPreviewEntry,
    setSelectedDetails,
    setDetailsOpen,
    isOptionDisabled,
    getOptionDisabledReason,
    t
  }), [filteredEntries, focusedIndex, selectedEntryKey, isFavorite, toggleFavorite, onValueChange, isOptionDisabled, getOptionDisabledReason, t]);
  return <>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between h-12 px-3 sm:px-4 bg-transparent">
						{selectedModel ? <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
								{(() => {
              if (selectedModelId === selectedModel.id && !selectedProviderDef) {
                return <Sparkles className="h-5 w-5 shrink-0 text-primary" />;
              }
              return getProviderLogo((selectedProviderId || selectedModel.mappings[0].providerId) as ProviderId);
            })()}
								<div className="flex flex-col items-start min-w-0 flex-1">
									<div className="flex items-center gap-1 max-w-full">
										<span className="font-medium truncate">
											{selectedModel.name}
										</span>
										{(() => {
                  const mappingForWarning = selectedModel.mappings.find(p_2 => p_2.providerId === selectedProviderId);
                  const isUnstable = mappingForWarning && isModelUnstable(mappingForWarning, selectedModel);
                  const isDeprecated = mappingForWarning?.deprecatedAt && new Date(mappingForWarning.deprecatedAt) <= new Date();
                  return isUnstable || isDeprecated ? <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-yellow-600 dark:text-yellow-500" /> : null;
                })()}
									</div>
									<span className="text-xs text-muted-foreground truncate max-w-full">
										{selectedModelId === selectedModel.id && !selectedProviderDef ? t("modelSelector.autoSelectProvider") : (selectedProviderDef ?? getProviderForModel(selectedModel, providers))?.name}
										{selectedMapping?.region && <span className="ml-1">({selectedMapping.region})</span>}
									</span>
								</div>
							</div> : <span className="truncate">
								{placeholder ?? t("modelSelector.selectModel")}
							</span>}
						<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-[300px] sm:w-[720px] p-0 z-99999" sideOffset={4} align="start">
					<div className="flex w-[300px] md:w-full">
						{/* Main content - model list & filters */}
						<div className="flex-1 w-[300px] md:w-[340px]">
							<Command shouldFilter={false}>
								<div className="flex items-center border-b px-3 w-[300px] md:w-full [&_[cmdk-input-wrapper]]:border-0">
									<CommandInput placeholder={t("modelSelector.searchModels")} value={searchQuery} onValueChange={setSearchQuery} onKeyDown={handleInputKeyDown} className="h-12" />
									<Popover open={filterOpen} onOpenChange={setFilterOpen}>
										<PopoverTrigger asChild>
											<Button variant="ghost" size="sm" className={cn("ml-2 h-8 w-8 p-0", hasActiveFilters && "text-primary")}>
												<Filter className="h-4 w-4" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-[calc(100vw-2rem)] sm:w-80 h-[400px] overflow-y-scroll md:h-full" style={{
                    zIndex: 100000
                  }} side={isMobile ? "bottom" : "right"} align={isMobile ? "end" : "start"}>
											<div className="space-y-3">
												<div className="flex items-center justify-between">
													<h4 className="font-medium">
														{t("modelSelector.filters")}
													</h4>
													{hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters}>
															{t("modelSelector.clearAll")}
														</Button>}
												</div>

												{/* Toggle filters group */}
												<div className="space-y-2">
													<div className="flex items-center justify-between">
														<Label htmlFor="show-root" className="text-sm cursor-pointer font-medium">
															{t("modelSelector.showOnlyRoot")}
														</Label>
														<Switch id="show-root" checked={filters.showOnlyRoot} onCheckedChange={checked => updateFilter("showOnlyRoot", checked)} />
													</div>
													<div className="flex items-center justify-between">
														<Label htmlFor="show-favorites" className="text-sm cursor-pointer font-medium">
															{t("modelSelector.showFavoritesOnly")}
														</Label>
														<Switch id="show-favorites" checked={filters.showFavoritesOnly} onCheckedChange={checked_0 => updateFilter("showFavoritesOnly", checked_0)} />
													</div>
													{providersWithKeys.size > 0 && <div className="flex items-center justify-between">
															<Label htmlFor="show-with-keys" className="text-sm cursor-pointer font-medium">
																{t("modelSelector.showOnlyWithKeys")}
															</Label>
															<Switch id="show-with-keys" checked={filters.showOnlyWithKeys} onCheckedChange={checked_1 => updateFilter("showOnlyWithKeys", checked_1)} />
														</div>}
												</div>

												<Separator />

												{/* Provider filter */}
												<div className="space-y-1.5">
													<Label className="text-sm font-medium">
														{t("modelSelector.providers")}
													</Label>
													<div className="space-y-1.5 max-h-28 overflow-y-auto">
														{availableProviders.map(provider_2 => {
                            const ProviderIcon = getProviderIcon(provider_2.id);
                            return <div key={provider_2.id} className="flex items-center space-x-2">
																	<Checkbox id={`provider-${provider_2.id}`} checked={filters.providers.includes(provider_2.id)} onCheckedChange={() => toggleProviderFilter(provider_2.id)} />
																	<Label htmlFor={`provider-${provider_2.id}`} className="flex items-center gap-2 text-sm cursor-pointer">
																		{ProviderIcon && <ProviderIcon className="h-3 w-3" style={{
                                  color: provider_2.color ?? undefined
                                }} />}
																		{provider_2.name}
																	</Label>
																</div>;
                          })}
													</div>
												</div>

												<Separator />

												{/* Capabilities filter */}
												<div className="space-y-1.5">
													<Label className="text-sm font-medium">
														{t("modelSelector.capabilities")}
													</Label>
													<div className="space-y-1.5 max-h-28 overflow-y-auto">
														{availableCapabilities.map(capability_0 => <div key={capability_0} className="flex items-center space-x-2">
																<Checkbox id={`capability-${capability_0}`} checked={filters.capabilities.includes(capability_0)} onCheckedChange={() => toggleCapabilityFilter(capability_0)} />
																<Label htmlFor={`capability-${capability_0}`} className="text-sm cursor-pointer">
																	{getCapabilityLabel(capability_0, t)}
																</Label>
															</div>)}
													</div>
												</div>

												<Separator />

												{/* Price range filter */}
												<div className="space-y-1.5">
													<Label className="text-sm font-medium">
														{t("modelSelector.priceRange")}
													</Label>
													<div className="space-y-1.5">
														{[{
                            value: "all",
                            label: t("modelSelector.allModels")
                          }, {
                            value: "free",
                            label: t("modelSelector.freeModels")
                          }, {
                            value: "low",
                            label: t("modelSelector.lowCost")
                          }, {
                            value: "medium",
                            label: t("modelSelector.mediumCost")
                          }, {
                            value: "high",
                            label: t("modelSelector.highCost")
                          }].map(option => <div key={option.value} className="flex items-center space-x-2">
																<Checkbox id={`price-${option.value}`} checked={filters.priceRange === option.value} onCheckedChange={() => updateFilter("priceRange", option.value)} />
																<Label htmlFor={`price-${option.value}`} className="text-sm cursor-pointer">
																	{option.label}
																</Label>
															</div>)}
													</div>
												</div>

												<Separator />

												{/* Stability filter */}
												<div className="flex items-center justify-between">
													<Label htmlFor="hide-unstable" className="text-sm cursor-pointer font-medium">
														{t("modelSelector.hideUnstable")}
													</Label>
													<Switch id="hide-unstable" checked={filters.hideUnstable} onCheckedChange={checked_2 => updateFilter("hideUnstable", checked_2)} />
												</div>
											</div>
										</PopoverContent>
									</Popover>
								</div>
								<div className="flex min-h-0">
									{providerSidebarOptions.length > 0 && <div className="flex h-[324px] w-14 shrink-0 flex-col border-r border-border bg-muted/20 sm:h-[424px]">
											<div className="flex-1 overflow-y-auto py-2">
												<div className="flex flex-col items-center gap-2 px-2">
													{providerSidebarOptions.map(provider_3 => {
                        const isActive = filters.providers.includes(provider_3.id);
                        return <button key={provider_3.id} type="button" title={provider_3.name ?? provider_3.id} aria-label={provider_3.name ?? provider_3.id} aria-pressed={isActive} onClick={() => toggleSidebarProviderFilter(provider_3.id)} className={cn("flex h-9 w-9 items-center justify-center rounded-md border border-transparent bg-transparent text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground", isActive && "border-primary/50 bg-primary/10 text-primary shadow-sm")}>
																{getCompactProviderLogo(provider_3)}
															</button>;
                      })}
												</div>
											</div>
										</div>}
									<div className="min-w-0 flex-1">
										<div className="px-3 py-1 text-xs text-muted-foreground">
											{t("modelSelector.modelsFound", {
                      count: filteredEntries.length
                    })}
										</div>
										{filteredEntries.length === 0 ? <div className="py-6 text-center text-sm">
												{t("modelSelector.noModelsFound")}
												{hasActiveFilters && <Button variant="link" size="sm" onClick={clearFilters} className="mt-2 block mx-auto">
														{t("modelSelector.clearFilters")}
													</Button>}
											</div> : <div ref={listContainerRef} className="h-[300px] sm:h-[400px]">
												<List style={{
                      height: "100%",
                      width: "100%"
                    }} rowComponent={ModelEntryRowComponent} rowCount={filteredEntries.length} rowHeight={ROW_HEIGHT} rowProps={rowProps} overscanCount={8} />
											</div>}
									</div>
								</div>
							</Command>
						</div>
						{/* Desktop preview panel */}
						<div className="hidden md:block w-[360px] border-l border-border bg-muted/40">
							<div className="p-4 space-y-3 h-full overflow-y-auto">
								{previewEntry ? <>
										<div className="flex items-center gap-3">
											{(() => {
                    if (!previewEntry.provider) {
                      return <div className="p-2 rounded-lg bg-primary/10">
															<Sparkles className="h-5 w-5 text-primary" />
														</div>;
                    }
                    const ProviderIcon_0 = getProviderIcon(previewEntry.provider.id);
                    return ProviderIcon_0 ? <div className="p-2 rounded-lg" style={{
                      backgroundColor: `${previewEntry.provider?.color}15`
                    }}>
														<ProviderIcon_0 className="h-5 w-5 dark:text-white" />
													</div> : null;
                  })()}
											<div className="flex-1 min-w-0">
												<div className="font-semibold text-sm truncate">
													{previewEntry.model.name}
												</div>
												<div className="text-xs text-muted-foreground truncate">
													{previewEntry.provider?.name ?? t("modelSelector.autoSelectProvider")}
													{previewEntry.mapping?.region && <span className="ml-1">
															({previewEntry.mapping.region})
														</span>}
												</div>
												<div className="text-[11px] text-muted-foreground capitalize truncate">
													{previewEntry.model.id !== "auto" ? t("modelSelector.familyLabel", {
                        family: previewEntry.model.family
                      }) : "-"}
												</div>
											</div>
										</div>

										{!previewEntry.provider ? <>
												<p className="text-xs text-muted-foreground leading-relaxed">
													{t("modelSelector.rootModelDescription")}
												</p>

												{(() => {
                    const aggregate = getRootAggregateInfo(previewEntry.model);
                    const isVideo = mode === "video" || !!previewEntry.model.output?.includes("video");
                    const minPerSec = isVideo ? getMinPerSecondPrice(previewEntry.model.mappings) : null;
                    const maxPerSec = isVideo ? getMaxPerSecondPrice(previewEntry.model.mappings) : null;
                    const hasPricingOrLimits = isVideo ? minPerSec !== null : aggregate.minInputPrice !== undefined || aggregate.minOutputPrice !== undefined || aggregate.maxContextSize !== undefined || aggregate.maxOutput !== undefined;
                    const hasImagePricing = !isVideo && (aggregate.minRequestPrice !== undefined && aggregate.minRequestPrice > 0 || aggregate.minImageInputPrice !== undefined || aggregate.minImageOutputPrice !== undefined);
                    const imageEstimate = mode === "image" ? getMinImageCostEstimate(previewEntry.model.mappings) : null;
                    const hasCapabilities = aggregate.capabilities.length > 0;
                    if (!hasPricingOrLimits && !hasImagePricing && !hasCapabilities && !imageEstimate) {
                      return null;
                    }
                    return <div className="space-y-3 pt-3 border-t border-dashed">
															{imageEstimate && <ImageEstimateCard estimate={imageEstimate} />}
															{hasPricingOrLimits && (isVideo ? <div className="space-y-2">
																		<h5 className="font-medium text-xs">
																			{t("modelSelector.videoPricing")}{" "}
																			<span className="text-[11px] font-normal text-muted-foreground">
																				({t("modelSelector.perSecond")})
																			</span>
																		</h5>
																		<div className="grid grid-cols-2 gap-3">
																			<div className="space-y-1">
																				<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																					{t("modelSelector.from")}
																				</span>
																				<p className="text-xs font-mono">
																					{minPerSec ?? t("modelSelector.notAvailable")}
																				</p>
																			</div>
																			<div className="space-y-1">
																				<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																					{t("modelSelector.upTo")}
																				</span>
																				<p className="text-xs font-mono">
																					{maxPerSec ?? t("modelSelector.notAvailable")}
																				</p>
																			</div>
																		</div>
																		{mode === "chat" && <div className="flex items-start gap-1.5 rounded-md bg-muted/50 px-2.5 py-2 text-xs text-muted-foreground">
																				<Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
																				<span>
																					{t("modelSelector.selectingModelOpensVideoStudio")}
																				</span>
																			</div>}
																	</div> : <div className="space-y-2">
																		<h5 className="font-medium text-xs">
																			{t("modelSelector.pricingLimits")}{" "}
																			<span className="text-[11px] font-normal text-muted-foreground">
																				({t("modelSelector.startsAt")})
																			</span>
																		</h5>
																		<div className="grid grid-cols-2 gap-3">
																			{(!previewIsImageGen || previewExpandTokens) && <>
																					<div className="space-y-1">
																						<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																							{t("modelSelector.input")}
																						</span>
																						<p className="text-xs font-mono">
																							{aggregate.minInputPrice !== undefined ? formatPrice(aggregate.minInputPrice) : t("modelSelector.notAvailable")}
																						</p>
																					</div>
																					<div className="space-y-1">
																						<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																							{t("modelSelector.output")}
																						</span>
																						<p className="text-xs font-mono">
																							{aggregate.minOutputPrice !== undefined ? formatPrice(aggregate.minOutputPrice) : t("modelSelector.notAvailable")}
																						</p>
																					</div>
																				</>}
																			<div className="space-y-1">
																				<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																					{t("modelSelector.context")}
																				</span>
																				<p className="text-xs font-mono">
																					{formatContextSize(aggregate.maxContextSize)}
																				</p>
																			</div>
																			<div className="space-y-1">
																				<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																					{t("modelSelector.maxOutput")}
																				</span>
																				<p className="text-xs font-mono">
																					{formatContextSize(aggregate.maxOutput)}
																				</p>
																			</div>
																		</div>
																		{previewIsImageGen && <button type="button" className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline" onClick={() => setPreviewExpandTokens(v => !v)}>
																				{previewExpandTokens ? t("modelSelector.hideTokenPricing") : t("modelSelector.expandDetails")}
																			</button>}
																	</div>)}

															{hasImagePricing && <div className="pt-2">
																	<div className="grid grid-cols-2 gap-3">
																		{aggregate.minRequestPrice !== undefined && aggregate.minRequestPrice > 0 && <div className="space-y-1">
																					<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																						{t("modelSelector.perRequest")}
																					</span>
																					<p className="text-xs font-mono">
																						$
																						{aggregate.minRequestPrice.toFixed(3)}
																						/req
																					</p>
																				</div>}
																		{aggregate.minImageInputPrice !== undefined && <div className="space-y-1">
																				<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																					{t("modelSelector.imageInput")}
																				</span>
																				<p className="text-xs font-mono">
																					{formatPrice(aggregate.minImageInputPrice)}
																				</p>
																			</div>}
																		{aggregate.minImageOutputPrice !== undefined && <div className="space-y-1">
																				<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																					{t("modelSelector.imageOutput")}
																				</span>
																				<p className="text-xs font-mono">
																					{formatPrice(aggregate.minImageOutputPrice)}
																				</p>
																			</div>}
																	</div>
																</div>}

															{hasCapabilities && previewEntry.model.id !== "auto" && <div className="space-y-1">
																		<h5 className="font-medium text-xs">
																			{t("modelSelector.capabilities")}
																		</h5>
																		<div className="flex flex-wrap gap-1">
																			{aggregate.capabilities.map(capability_1 => {
                            const {
                              Icon
                            } = getCapabilityIconConfig(capability_1);
                            return <Badge key={capability_1} variant="secondary" className="text-[10px] px-1.5 py-0.5 flex items-center gap-1">
																							{Icon && <Icon size={12} />}
																							{getCapabilityLabel(capability_1, t)}
																						</Badge>;
                          })}
																		</div>
																	</div>}
														</div>;
                  })()}
											</> : <>
												{previewEntry.provider?.description && <p className="text-xs text-muted-foreground leading-relaxed">
														{previewEntry.provider.description}
													</p>}

												<div className="space-y-2">
													<h5 className="font-medium text-xs">
														{mode === "video" || previewEntry.model.output?.includes("video") ? t("modelSelector.videoPricing") : t("modelSelector.pricingLimits")}
													</h5>
													{mode === "video" || previewEntry.model.output?.includes("video") ? <>
															<div className="grid grid-cols-1 gap-3">
																<div className="space-y-1">
																	<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																		{t("modelSelector.perSecond")}
																	</span>
																	<p className="text-xs font-mono">
																		{previewEntry.mapping?.perSecondPrice ? formatPerSecondPrice(previewEntry.mapping.perSecondPrice) : t("modelSelector.notAvailable")}
																	</p>
																</div>
															</div>
															{mode === "chat" && <div className="flex items-start gap-1.5 rounded-md bg-muted/50 px-2.5 py-2 text-xs text-muted-foreground">
																	<Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
																	<span>
																		{t("modelSelector.selectingModelOpensVideoStudio")}
																	</span>
																</div>}
														</> : <>
															<div className="grid grid-cols-2 gap-3">
																{(!previewIsImageGen || previewExpandTokens) && <>
																		<div className="space-y-1">
																			<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																				{t("modelSelector.input")}
																			</span>
																			<p className="text-xs font-mono">
																				{(() => {
                                const price = getMappingPriceInfo(previewEntry.mapping, "input");
                                if (price.original && price.discounted && price.original !== price.discounted) {
                                  return <>
																								<span className="line-through text-muted-foreground">
																									{price.original}
																								</span>{" "}
																								<span className="text-green-500">
																									{price.discounted}
																								</span>
																							</>;
                                }
                                return price.label;
                              })()}
																			</p>
																		</div>
																		<div className="space-y-1">
																			<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																				{t("modelSelector.output")}
																			</span>
																			<p className="text-xs font-mono">
																				{(() => {
                                const price_0 = getMappingPriceInfo(previewEntry.mapping, "output");
                                if (price_0.original && price_0.discounted && price_0.original !== price_0.discounted) {
                                  return <>
																								<span className="line-through text-muted-foreground">
																									{price_0.original}
																								</span>{" "}
																								<span className="text-green-500">
																									{price_0.discounted}
																								</span>
																							</>;
                                }
                                return price_0.label;
                              })()}
																			</p>
																		</div>
																	</>}
																<div className="space-y-1">
																	<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																		{t("modelSelector.context")}
																	</span>
																	<p className="text-xs font-mono">
																		{formatContextSize(previewEntry.mapping?.contextSize)}
																	</p>
																</div>
																<div className="space-y-1">
																	<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																		{t("modelSelector.maxOutput")}
																	</span>
																	<p className="text-xs font-mono">
																		{formatContextSize(previewEntry.mapping?.maxOutput)}
																	</p>
																</div>
															</div>
															{previewIsImageGen && <button type="button" className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline" onClick={() => setPreviewExpandTokens(v_0 => !v_0)}>
																	{previewExpandTokens ? t("modelSelector.hideTokenPricing") : t("modelSelector.expandDetails")}
																</button>}
														</>}
													{previewEntry.mapping?.cachedInputPrice && (!previewIsImageGen || previewExpandTokens) && <div className="pt-2 border-t border-dashed">
																<div className="space-y-1">
																	<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																		{t("modelSelector.cachedInput")}
																	</span>
																	<p className="text-xs font-mono text-green-600 dark:text-green-400">
																		{(() => {
                            const price_1 = getMappingPriceInfo(previewEntry.mapping, "cachedInput");
                            if (price_1.original && price_1.discounted && price_1.original !== price_1.discounted) {
                              return <>
																						<span className="line-through text-muted-foreground">
																							{price_1.original}
																						</span>{" "}
																						<span className="text-green-500">
																							{price_1.discounted}
																						</span>
																					</>;
                            }
                            return price_1.label;
                          })()}
																	</p>
																</div>
															</div>}
													{mode === "image" && (() => {
                      const est = estimateImageCost(previewEntry.mapping);
                      if (!est) {
                        return null;
                      }
                      return <div className="pt-2 mt-2 rounded-md border border-primary/30 bg-primary/5 p-2.5">
																	<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																		{t("modelSelector.estimatedPerImage")}
																	</span>
																	<p className="text-sm font-mono font-semibold text-primary">
																		~{formatImageCost(est.discounted)}
																	</p>
																	<p className="text-[10px] text-muted-foreground">
																		{t("modelSelector.typicalImageOutput")}
																	</p>
																</div>;
                    })()}
													{/* Image Generation Pricing */}
													{(previewEntry.mapping?.requestPrice ?? previewEntry.mapping?.imageInputPrice) && <div className="pt-2">
															<div className="grid grid-cols-2 gap-3">
																{previewEntry.mapping?.requestPrice && parseFloat(previewEntry.mapping.requestPrice) > 0 && <div className="space-y-1">
																			<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																				{t("modelSelector.perRequest")}
																			</span>
																			<p className="text-xs font-mono">
																				{(() => {
                              const price_2 = getMappingPriceInfo(previewEntry.mapping, "request");
                              if (price_2.original && price_2.discounted && price_2.original !== price_2.discounted) {
                                return <>
																								<span className="line-through text-muted-foreground">
																									{price_2.original}
																								</span>{" "}
																								<span className="text-green-500">
																									{price_2.discounted}
																								</span>
																							</>;
                              }
                              return price_2.label;
                            })()}
																			</p>
																		</div>}
																{previewEntry.mapping?.imageInputPrice !== undefined && <div className="space-y-1">
																		<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																			{t("modelSelector.imageInput")}
																		</span>
																		<p className="text-xs font-mono">
																			{(() => {
                              const price_3 = getMappingPriceInfo(previewEntry.mapping, "imageInput");
                              if (price_3.original && price_3.discounted && price_3.original !== price_3.discounted) {
                                return <>
																							<span className="line-through text-muted-foreground">
																								{price_3.original}
																							</span>{" "}
																							<span className="text-green-500">
																								{price_3.discounted}
																							</span>
																						</>;
                              }
                              return price_3.label;
                            })()}
																		</p>
																	</div>}
															</div>
														</div>}
												</div>

												{(() => {
                    const caps = getMappingCapabilities(previewEntry.mapping, previewEntry.model);
                    return caps.length > 0 ? <div className="space-y-1">
															<h5 className="font-medium text-xs">
																{t("modelSelector.capabilities")}
															</h5>
															<div className="flex flex-wrap gap-1">
																{caps.map(capability_2 => {
                          const {
                            Icon: Icon_0
                          } = getCapabilityIconConfig(capability_2);
                          return <Badge key={capability_2} variant="secondary" className="text-[10px] px-1.5 py-0.5 flex items-center gap-1">
																			{Icon_0 && <Icon_0 size={12} />}
																			{getCapabilityLabel(capability_2, t)}
																		</Badge>;
                        })}
															</div>
														</div> : null;
                  })()}
											</>}
									</> : <p className="text-xs text-muted-foreground">
										Hover a model to see details.
									</p>}
							</div>
						</div>
					</div>
				</PopoverContent>
			</Popover>

			{/* Model Details Dialog */}
			<Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
				<DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
					{selectedDetails && <>
							<DialogHeader>
								<DialogTitle className="flex items-center gap-3">
									{(() => {
                if (!selectedDetails.provider) {
                  return <div className="p-2 rounded-lg bg-primary/10">
													<Sparkles className="h-6 w-6 text-primary" />
												</div>;
                }
                const ProviderIcon_1 = getProviderIcon(selectedDetails.provider.id);
                return ProviderIcon_1 ? <div className="p-2 rounded-lg" style={{
                  backgroundColor: `${selectedDetails.provider?.color}15`
                }}>
												<ProviderIcon_1 className="h-6 w-6 dark:text-white" />
											</div> : null;
              })()}
									<div className="flex-1">
										<div className="font-semibold text-base">
											{selectedDetails.model.name}
										</div>
										<div className="text-sm text-muted-foreground font-normal">
											{selectedDetails.provider?.name ?? t("modelSelector.autoSelectProvider")}
											{selectedDetails.mapping?.region && <span className="ml-1">
													({selectedDetails.mapping.region})
												</span>}
										</div>
										<div className="text-xs text-muted-foreground font-normal capitalize">
											{t("modelSelector.familyLabel", {
                    family: selectedDetails.model.family
                  })}
										</div>
									</div>
									{selectedDetails.provider?.website && <Button variant="ghost" size="sm" asChild>
											<a href={selectedDetails.provider.website} target="_blank" rel="noopener noreferrer" className="h-8 w-8 p-0">
												<ExternalLink className="h-3 w-3" />
											</a>
										</Button>}
								</DialogTitle>
							</DialogHeader>

							<div className="space-y-4">
								{!selectedDetails.provider ? <div className="space-y-4">
										<p className="text-sm text-muted-foreground leading-relaxed">
											{t("modelSelector.rootModelDescription")}
										</p>

										{(() => {
                const aggregate_0 = getRootAggregateInfo(selectedDetails.model);
                const imageEstimate_0 = getMinImageCostEstimate(selectedDetails.model.mappings);
                const hasPricingOrLimits_0 = aggregate_0.minInputPrice !== undefined || aggregate_0.minOutputPrice !== undefined || aggregate_0.maxContextSize !== undefined || aggregate_0.maxOutput !== undefined;
                const hasImagePricing_0 = aggregate_0.minRequestPrice !== undefined && aggregate_0.minRequestPrice > 0 || aggregate_0.minImageInputPrice !== undefined || aggregate_0.minImageOutputPrice !== undefined;
                const hasCapabilities_0 = aggregate_0.capabilities.length > 0;
                if (!hasPricingOrLimits_0 && !hasImagePricing_0 && !hasCapabilities_0 && !imageEstimate_0) {
                  return null;
                }
                return <div className="space-y-4">
													{imageEstimate_0 && <ImageEstimateCard estimate={imageEstimate_0} />}
													{hasPricingOrLimits_0 && <div className="space-y-3">
															<h5 className="font-medium text-sm">
																{t("modelSelector.pricingLimits")}{" "}
																<span className="text-xs font-normal text-muted-foreground">
																	({t("modelSelector.startsAt")})
																</span>
															</h5>
															<div className="grid grid-cols-2 gap-3">
																{(!selectedIsImageGen || selectedExpandTokens) && <>
																		<div className="space-y-1">
																			<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																				{t("modelSelector.input")}
																			</span>
																			<p className="text-sm font-mono">
																				{aggregate_0.minInputPrice !== undefined ? formatPrice(aggregate_0.minInputPrice) : t("modelSelector.notAvailable")}
																			</p>
																		</div>
																		<div className="space-y-1">
																			<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																				{t("modelSelector.output")}
																			</span>
																			<p className="text-sm font-mono">
																				{aggregate_0.minOutputPrice !== undefined ? formatPrice(aggregate_0.minOutputPrice) : t("modelSelector.notAvailable")}
																			</p>
																		</div>
																	</>}
																<div className="space-y-1">
																	<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																		{t("modelSelector.context")}
																	</span>
																	<p className="text-sm font-mono">
																		{formatContextSize(aggregate_0.maxContextSize)}
																	</p>
																</div>
																<div className="space-y-1">
																	<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																		{t("modelSelector.maxOutput")}
																	</span>
																	<p className="text-sm font-mono">
																		{formatContextSize(aggregate_0.maxOutput)}
																	</p>
																</div>
															</div>
															{selectedIsImageGen && <button type="button" className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline" onClick={() => setSelectedExpandTokens(v_1 => !v_1)}>
																	{selectedExpandTokens ? t("modelSelector.hideTokenPricing") : t("modelSelector.expandDetails")}
																</button>}
														</div>}

													{hasImagePricing_0 && <div className="pt-2">
															<div className="grid grid-cols-2 gap-3">
																{aggregate_0.minRequestPrice !== undefined && aggregate_0.minRequestPrice > 0 && <div className="space-y-1">
																			<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																				{t("modelSelector.perRequest")}
																			</span>
																			<p className="text-sm font-mono">
																				${aggregate_0.minRequestPrice.toFixed(3)}
																				/req
																			</p>
																		</div>}
																{aggregate_0.minImageInputPrice !== undefined && <div className="space-y-1">
																		<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																			{t("modelSelector.imageInput")}
																		</span>
																		<p className="text-sm font-mono">
																			{formatPrice(aggregate_0.minImageInputPrice)}
																		</p>
																	</div>}
																{aggregate_0.minImageOutputPrice !== undefined && <div className="space-y-1">
																		<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																			{t("modelSelector.imageOutput")}
																		</span>
																		<p className="text-sm font-mono">
																			{formatPrice(aggregate_0.minImageOutputPrice)}
																		</p>
																	</div>}
															</div>
														</div>}

													{hasCapabilities_0 && <div className="space-y-2">
															<h5 className="font-medium text-sm">
																{t("modelSelector.capabilities")}
															</h5>
															<div className="flex flex-wrap gap-1.5">
																{aggregate_0.capabilities.map(capability_3 => <Badge key={capability_3} variant="secondary" className="text-xs px-2 py-1">
																		{getCapabilityLabel(capability_3, t)}
																	</Badge>)}
															</div>
														</div>}
												</div>;
              })()}
									</div> : <>
										{selectedDetails.provider?.description && <>
												<p className="text-sm text-muted-foreground leading-relaxed">
													{selectedDetails.provider.description}
												</p>
												<Separator />
											</>}

										{(() => {
                const imageEstimate_1 = selectedDetails.mapping ? estimateImageCost(selectedDetails.mapping) : null;
                return imageEstimate_1 ? <ImageEstimateCard estimate={imageEstimate_1} /> : null;
              })()}

										<div className="space-y-3">
											<h5 className="font-medium text-sm">
												{t("modelSelector.pricingLimits")}
											</h5>
											<div className="grid grid-cols-2 gap-3">
												{(!selectedIsImageGen || selectedExpandTokens) && <>
														<div className="space-y-1">
															<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																{t("modelSelector.input")}
															</span>
															<p className="text-sm font-mono">
																{(() => {
                          const price_4 = getMappingPriceInfo(selectedDetails.mapping, "input");
                          if (price_4.original && price_4.discounted && price_4.original !== price_4.discounted) {
                            return <>
																				<span className="line-through text-muted-foreground">
																					{price_4.original}
																				</span>{" "}
																				<span className="text-green-500">
																					{price_4.discounted}
																				</span>
																			</>;
                          }
                          return price_4.label;
                        })()}
															</p>
														</div>
														<div className="space-y-1">
															<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																{t("modelSelector.output")}
															</span>
															<p className="text-sm font-mono">
																{(() => {
                          const price_5 = getMappingPriceInfo(selectedDetails.mapping, "output");
                          if (price_5.original && price_5.discounted && price_5.original !== price_5.discounted) {
                            return <>
																				<span className="line-through text-muted-foreground">
																					{price_5.original}
																				</span>{" "}
																				<span className="text-green-500">
																					{price_5.discounted}
																				</span>
																			</>;
                          }
                          return price_5.label;
                        })()}
															</p>
														</div>
													</>}
												<div className="space-y-1">
													<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
														{t("modelSelector.context")}
													</span>
													<p className="text-sm font-mono">
														{formatContextSize(selectedDetails.mapping?.contextSize)}
													</p>
												</div>
												<div className="space-y-1">
													<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
														{t("modelSelector.maxOutput")}
													</span>
													<p className="text-sm font-mono">
														{formatContextSize(selectedDetails.mapping?.maxOutput)}
													</p>
												</div>
											</div>
											{selectedIsImageGen && <button type="button" className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline" onClick={() => setSelectedExpandTokens(v_2 => !v_2)}>
													{selectedExpandTokens ? t("modelSelector.hideTokenPricing") : t("modelSelector.expandDetails")}
												</button>}
											{selectedDetails.mapping?.cachedInputPrice && (!selectedIsImageGen || selectedExpandTokens) && <div className="pt-2 border-t border-dashed">
														<div className="space-y-1">
															<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																{t("modelSelector.cachedInput")}
															</span>
															<p className="text-sm font-mono text-green-600 dark:text-green-400">
																{(() => {
                        const price_6 = getMappingPriceInfo(selectedDetails.mapping, "cachedInput");
                        if (price_6.original && price_6.discounted && price_6.original !== price_6.discounted) {
                          return <>
																				<span className="line-through text-muted-foreground">
																					{price_6.original}
																				</span>{" "}
																				<span className="text-green-500">
																					{price_6.discounted}
																				</span>
																			</>;
                        }
                        return price_6.label;
                      })()}
															</p>
														</div>
													</div>}
											{/* Image Generation Pricing */}
											{(selectedDetails.mapping?.requestPrice ?? selectedDetails.mapping?.imageInputPrice) && <div className="pt-2 border-t border-dashed">
													<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-2">
														{t("modelSelector.imagePricing")}
													</span>
													<div className="grid grid-cols-2 gap-3">
														{selectedDetails.mapping?.requestPrice && parseFloat(selectedDetails.mapping.requestPrice) > 0 && <div className="space-y-1">
																	<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																		{t("modelSelector.perRequest")}
																	</span>
																	<p className="text-sm font-mono">
																		{(() => {
                          const price_7 = getMappingPriceInfo(selectedDetails.mapping, "request");
                          if (price_7.original && price_7.discounted && price_7.original !== price_7.discounted) {
                            return <>
																						<span className="line-through text-muted-foreground">
																							{price_7.original}
																						</span>{" "}
																						<span className="text-green-500">
																							{price_7.discounted}
																						</span>
																					</>;
                          }
                          return price_7.label;
                        })()}
																	</p>
																</div>}
														{selectedDetails.mapping?.imageInputPrice !== undefined && <div className="space-y-1">
																<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																	{t("modelSelector.imageInput")}
																</span>
																<p className="text-sm font-mono">
																	{(() => {
                          const price_8 = getMappingPriceInfo(selectedDetails.mapping, "imageInput");
                          if (price_8.original && price_8.discounted && price_8.original !== price_8.discounted) {
                            return <>
																					<span className="line-through text-muted-foreground">
																						{price_8.original}
																					</span>{" "}
																					<span className="text-green-500">
																						{price_8.discounted}
																					</span>
																				</>;
                          }
                          return price_8.label;
                        })()}
																</p>
															</div>}
													</div>
												</div>}
										</div>

										<Separator />

										{(() => {
                const caps_0 = getMappingCapabilities(selectedDetails.mapping, selectedDetails.model);
                return caps_0.length > 0 ? <div className="space-y-2">
													<h5 className="font-medium text-sm">
														{t("modelSelector.capabilities")}
													</h5>
													<div className="flex flex-wrap gap-1.5">
														{caps_0.map(capability_4 => {
                      const {
                        Icon: Icon_1
                      } = getCapabilityIconConfig(capability_4);
                      return <Badge key={capability_4} variant="secondary" className="text-xs px-2 py-1 flex items-center gap-1.5">
																	{Icon_1 && <Icon_1 size={14} />}
																	{getCapabilityLabel(capability_4, t)}
																</Badge>;
                    })}
													</div>
												</div> : null;
              })()}
									</>}
							</div>
						</>}
				</DialogContent>
			</Dialog>
		</>;
}